import React, { useEffect, useState, useCallback } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ToastContainer, toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchAllAgentConfigs,
  createAgentConfig,
} from "../../redux/actions/agentConfigActions";
import { fetchOrganizations } from "../../redux/actions/organizationActions";
import { RootState, AppDispatch } from "../../redux/store";
import { format } from "date-fns";
import {
  AvatarLook,
  AvatarVoice,
  VoicePersonality,
  AvatarEnvironment,
  AvatarLocation,
  AvatarPose,
  AvatarShot,
  AvatarLanguage,
  VoiceEmotion,
  getAvatarLookDescription,
  getAvatarVoiceDescription,
  getVoicePersonalityDescription,
  getEnvironmentDescription,
  getAvatarLocationDescription,
  getAvatarPoseDescription,
  getAvatarShotDescription,
  getAvatarLanguageDescription,
  getVoiceEmotionDescription,
} from "../../types/avatarConfig";
import { AgentConfig } from "../../redux/reducers/agentConfigReducer";
import { getOrganizationDisplayName } from "../../utils/render";
import Fuse from "fuse.js";

interface AgentConfigsTabProps {
  organizationId?: string;
}

const AgentConfigsTab: React.FC<AgentConfigsTabProps> = ({
  organizationId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { agentConfigs, loading, error } = useSelector(
    (state: RootState) => state.agentConfigs
  );
  const { organizations, loading: orgsLoading } = useSelector(
    (state: RootState) => state.organizations
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newBotName, setNewBotName] = useState("Bella");
  const [newBotDescription, setNewBotDescription] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newDisplayNameTouched, setNewDisplayNameTouched] = useState(false);
  const [newOverridePersonalityGuidance, setNewOverridePersonalityGuidance] =
    useState("");
  const [newOrganizationId, setNewOrganizationId] = useState("");
  const [newAvatarLook, setNewAvatarLook] = useState("");
  const [newAvatarVoice, setNewAvatarVoice] = useState("");
  const [newVoicePersonality, setNewVoicePersonality] = useState("");
  const [newAvatarDefaultEmotion, setNewAvatarDefaultEmotion] = useState("");
  const [newAvatarLanguage, setNewAvatarLanguage] = useState("");
  const [newEnvironment, setNewEnvironment] = useState("");
  const [newAvatarLocation, setNewAvatarLocation] = useState("");
  const [newAvatarPose, setNewAvatarPose] = useState("");
  const [newAvatarShot, setNewAvatarShot] = useState("");

  useEffect(() => {
    // If a toast message was passed via navigation state, show it.
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      // Clear the state so the toast isn't shown again.
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Fetch all agent configs and organizations when the component mounts
  useEffect(() => {
    dispatch(fetchAllAgentConfigs());
    // Only fetch organizations if we're not already in an organization context
    // This prevents duplicate API calls when used within OrganizationView
    if (!organizationId) {
      dispatch(fetchOrganizations());
    }
  }, [dispatch, organizationId]);

  // Set organization ID if provided as prop
  useEffect(() => {
    if (organizationId) {
      setNewOrganizationId(organizationId);
    }
  }, [organizationId]);

  const handleViewAgentConfig = useCallback(
    (id: string) => {
      navigate(`/dashboard/agent-configs/${id}`);
    },
    [navigate]
  );

  const handleCreateAgentConfig = useCallback(() => {
    setDialogOpen(true);
    // Mark as touched immediately since it starts empty
    setNewDisplayNameTouched(true);
  }, []);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewBotName("Bella");
    setNewBotDescription("");
    setNewDisplayName("");
    setNewDisplayNameTouched(false);
    setNewOverridePersonalityGuidance("");
    setNewOrganizationId(organizationId || "");
    setNewAvatarLook("");
    setNewAvatarVoice("");
    setNewVoicePersonality("");
    setNewAvatarDefaultEmotion("");
    setNewAvatarLanguage("");
    setNewEnvironment("");
    setNewAvatarLocation("");
    setNewAvatarPose("");
    setNewAvatarShot("");
  };

  const handleCreateAgentConfigSubmit = () => {
    if (!newBotName || !newBotDescription) {
      toast.error("Bot Name and Bot Description are required");
      return;
    }

    const agentConfigData: AgentConfig = {
      organization_id: newOrganizationId || undefined,
      bot_name: newBotName,
      bot_description: newBotDescription,
      display_name: newDisplayName || undefined,
      override_personality_guidance:
        newOverridePersonalityGuidance || undefined,
    };

    // Only include avatar_config if any avatar fields are set
    if (newAvatarLook || newAvatarVoice || newVoicePersonality || newAvatarDefaultEmotion || newAvatarLanguage || newEnvironment || newAvatarLocation || newAvatarPose || newAvatarShot) {
      agentConfigData.avatar_config = {
        avatar_look: newAvatarLook || undefined,
        avatar_voice: newAvatarVoice || undefined,
        avatar_voice_personality: newVoicePersonality || undefined,
        avatar_default_emotion: newAvatarDefaultEmotion || undefined,
        avatar_language: newAvatarLanguage || undefined,
        avatar_location: newAvatarLocation || undefined,
        avatar_pose: newAvatarPose || undefined,
        environment: newEnvironment || undefined,
        shot: newAvatarShot || undefined,
      };
    }

    dispatch(createAgentConfig(agentConfigData))
      .unwrap()
      .then((config) => {
        toast.success("Agent Config created successfully");
        handleDialogClose();
        navigate(`/dashboard/agent-configs/${config.id}`, {
          state: { toastMessage: "Agent Config created successfully" },
        });
      })
      .catch((error) => {
        toast.error(`Failed to create agent config: ${error.message}`);
      });
  };

  // Filter agent configs by organization if organizationId is provided
  let filteredAgentConfigs = organizationId
    ? agentConfigs.filter((config) => config.organization_id === organizationId)
    : agentConfigs;

  // Apply search filter
  if (searchQuery) {
    const fuse = new Fuse(filteredAgentConfigs, {
      keys: ["bot_name", "display_name", "id"],
      threshold: 0.3,
    });
    filteredAgentConfigs = fuse.search(searchQuery).map((result) => result.item);
  }

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Config ID",
      flex: 1.5,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "display_name",
      headerName: "Display Name",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => params || "-",
    },
    {
      field: "bot_name",
      headerName: "Bot Name",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "bot_description",
      headerName: "Bot Description",
      flex: 2,
      minWidth: 250,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => {
        // Truncate long descriptions
        const desc = params || "";
        return desc.length > 100 ? desc.substring(0, 100) + "..." : desc;
      },
    },
    {
      field: "organization_id",
      headerName: "Organization",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => {
        if (!params) return "None";
        const org = organizations.find(o => o.id === params);
        return org ? getOrganizationDisplayName(org) : params;
      },
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => {
        return params
          ? format(new Date(params), "yyyy-MM-dd HH:mm:ss")
          : "Never";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      disableColumnMenu: true,
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleViewAgentConfig(params.row.id)}
        >
          View
        </Button>
      ),
      headerAlign: "center",
      align: "center",
    },
  ];

  if (loading && agentConfigs.length === 0) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, mt: 3 }}>
        <TextField
          label="Search Agent Configs"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by bot name, display name, or ID"
          sx={{ flex: 1, maxWidth: 400 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateAgentConfig}
          size="medium"
          sx={{ fontSize: "0.9rem", padding: "6px 16px" }}
        >
          Create Agent Config
        </Button>
      </Box>

      <Box sx={{ height: organizationId ? "40vh" : "70vh", width: "100%" }}>
        <DataGrid
          rows={filteredAgentConfigs}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "created_at", sort: "desc" }],
            },
          }}
        />
      </Box>

      {/* Dialog for creating a new agent config */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle>Create New Agent Config</DialogTitle>
        <DialogContent sx={{ overflow: 'auto', pb: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the agent configuration details. A unique ID will be generated
            automatically.
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel id="organization-select-label">
              Organization
            </InputLabel>
            <Select
              labelId="organization-select-label"
              value={newOrganizationId}
              onChange={(e) => setNewOrganizationId(e.target.value)}
              label="Organization"
              disabled={!!organizationId}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {orgsLoading ? (
                <MenuItem disabled>Loading organizations...</MenuItem>
              ) : (
                organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))
              )}
            </Select>
            {organizationId && (
              <FormHelperText>
                Organization is pre-selected based on current context
              </FormHelperText>
            )}
          </FormControl>
          <TextField
            label="Bot Name"
            value={newBotName}
            onChange={(e) => setNewBotName(e.target.value)}
            fullWidth
            margin="normal"
            required
            helperText="The name of the bot (defaults to 'Bella')"
          />
          <TextField
            label="Display Name"
            value={newDisplayName}
            onChange={(e) => setNewDisplayName(e.target.value)}
            onBlur={() => setNewDisplayNameTouched(true)}
            error={newDisplayNameTouched && newDisplayName.trim() === ""}
            helperText={
              newDisplayNameTouched && newDisplayName.trim() === ""
                ? "Display Name cannot be empty. A user-friendly display name (e.g., 'Sales Training Bot')"
                : "A user-friendly display name (e.g., 'Sales Training Bot')"
            }
            fullWidth
            margin="normal"
            placeholder="e.g., Sales Training Bot"
            required
          />
          <TextField
            label="Bot Description"
            value={newBotDescription}
            onChange={(e) => setNewBotDescription(e.target.value)}
            fullWidth
            margin="normal"
            required
            multiline
            rows={3}
            helperText="A description of the bot's purpose and capabilities"
          />
          
          {/* Avatar Configuration Accordion */}
          <Accordion sx={{ mt: 2 }} defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="avatar-config-content"
              id="avatar-config-header"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Avatar Configuration</Typography>
                <Typography variant="caption" color="text.secondary">
                  (Optional - Customize avatar appearance and environment)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="avatar-look-label">Avatar Look</InputLabel>
                    <Select
                      labelId="avatar-look-label"
                      value={newAvatarLook}
                      onChange={(e) => setNewAvatarLook(e.target.value)}
                      label="Avatar Look"
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {Object.entries(AvatarLook).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key} - {getAvatarLookDescription(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the avatar's visual appearance
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="avatar-voice-label">Avatar Voice</InputLabel>
                    <Select
                      labelId="avatar-voice-label"
                      value={newAvatarVoice}
                      onChange={(e) => setNewAvatarVoice(e.target.value)}
                      label="Avatar Voice"
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {Object.entries(AvatarVoice).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key} - {getAvatarVoiceDescription(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the avatar's voice settings
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="voice-personality-label">Voice Personality</InputLabel>
                    <Select
                      labelId="voice-personality-label"
                      value={newVoicePersonality}
                      onChange={(e) => setNewVoicePersonality(e.target.value)}
                      label="Voice Personality"
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {Object.entries(VoicePersonality).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key} - {getVoicePersonalityDescription(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the avatar's voice personality filter
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="default-emotion-label">Default Emotion</InputLabel>
                    <Select
                      labelId="default-emotion-label"
                      value={newAvatarDefaultEmotion}
                      onChange={(e) => setNewAvatarDefaultEmotion(e.target.value)}
                      label="Default Emotion"
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {Object.entries(VoiceEmotion).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key} - {getVoiceEmotionDescription(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Default emotion when nothing is coming from the model
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="avatar-language-label">Language</InputLabel>
                    <Select
                      labelId="avatar-language-label"
                      value={newAvatarLanguage}
                      onChange={(e) => setNewAvatarLanguage(e.target.value)}
                      label="Language"
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {Object.entries(AvatarLanguage).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key} - {getAvatarLanguageDescription(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Preferred language for the avatar to speak
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="environment-label">Environment</InputLabel>
                    <Select
                      labelId="environment-label"
                      value={newEnvironment}
                      onChange={(e) => setNewEnvironment(e.target.value)}
                      label="Environment"
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {Object.entries(AvatarEnvironment).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key} - {getEnvironmentDescription(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the environment setting for the avatar
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="avatar-location-label">Avatar Location</InputLabel>
                    <Select
                      labelId="avatar-location-label"
                      value={newAvatarLocation}
                      onChange={(e) => setNewAvatarLocation(e.target.value)}
                      label="Avatar Location"
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {Object.entries(AvatarLocation).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key} - {getAvatarLocationDescription(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the avatar's location in the scene
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="avatar-pose-label">Avatar Pose</InputLabel>
                    <Select
                      labelId="avatar-pose-label"
                      value={newAvatarPose}
                      onChange={(e) => setNewAvatarPose(e.target.value)}
                      label="Avatar Pose"
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {Object.entries(AvatarPose).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key} - {getAvatarPoseDescription(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the avatar's pose
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="avatar-shot-label">Camera Shot</InputLabel>
                    <Select
                      labelId="avatar-shot-label"
                      value={newAvatarShot}
                      onChange={(e) => setNewAvatarShot(e.target.value)}
                      label="Camera Shot"
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {Object.entries(AvatarShot).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key} - {getAvatarShotDescription(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the camera shot framing
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          
          <TextField
            label="Override Personality Guidance"
            value={newOverridePersonalityGuidance}
            onChange={(e) => setNewOverridePersonalityGuidance(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            helperText="Optional: Override the default personality guidance (leave empty to use defaults)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleCreateAgentConfigSubmit}
            color="primary"
            disabled={!newBotName || !newBotDescription || newDisplayName.trim() === ""}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentConfigsTab;
